import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  Building2,
  Database,
  Globe,
  HardDrive,
  Settings as SettingsIcon,
  CreditCard,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { formatBytes, formatRelative } from '@/lib/format';

export const Route = createFileRoute('/_authed/sites/$siteId')({
  component: SiteDetailPage,
});

function SiteDetailPage() {
  const { siteId } = Route.useParams();
  const { data, isLoading } = useQuery({
    queryKey: ['site', siteId],
    queryFn: async () => {
      try {
        return await api.get<{
          site: {
            id: string;
            name: string;
            subdomain: string;
            plan: string;
            status: string;
            createdAt: number;
            ownerEmail: string;
          };
          stats: { visits: number; storage: number; bandwidth: number };
          logs: Array<{ ts: number; level: string; message: string }>;
          billing: { plan: string; renewsAt: number; amount: number };
        }>(`/admin/sites/${siteId}`);
      } catch {
        return {
          site: {
            id: siteId,
            name: 'Demo Site',
            subdomain: 'demo.cms.express',
            plan: 'GOLD',
            status: 'active',
            createdAt: Date.now() - 86400_000 * 30,
            ownerEmail: 'demo@empresa.com',
          },
          stats: { visits: 12450, storage: 1.2e9, bandwidth: 3.4e9 },
          logs: Array.from({ length: 6 }, (_, i) => ({
            ts: Date.now() - i * 3600_000,
            level: ['info', 'warn', 'error'][i % 3] as string,
            message: `Sample log message #${i + 1}`,
          })),
          billing: { plan: 'GOLD', renewsAt: Date.now() + 86400_000 * 15, amount: 49 },
        };
      }
    },
    enabled: !!siteId && siteId !== 'new',
  });

  if (siteId === 'new') {
    return <NewSitePlaceholder />;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          {isLoading ? (
            <Skeleton className="h-9 w-72" />
          ) : (
            <h1 className="text-3xl font-bold tracking-tight">{data?.site.name}</h1>
          )}
          <p className="text-muted-foreground">
            {data?.site.subdomain} · {data?.site.ownerEmail}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="success">{data?.site.status}</Badge>
          <Badge variant="warning">{data?.site.plan}</Badge>
          <Button variant="outline" size="sm">
            <SettingsIcon className="mr-2 h-4 w-4" />
            Ajustes
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="resources">Recursos</TabsTrigger>
          <TabsTrigger value="logs">Logs</TabsTrigger>
          <TabsTrigger value="billing">Facturación</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm">Visitas</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{data?.stats.visits.toLocaleString('es-ES')}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm">Storage</CardTitle>
                <HardDrive className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatBytes(data?.stats.storage ?? 0)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm">Bandwidth</CardTitle>
                <Globe className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatBytes(data?.stats.bandwidth ?? 0)}</div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="resources">
          <Card>
            <CardHeader>
              <CardTitle>Recursos aprovisionados</CardTitle>
              <CardDescription>Workers, D1 y R2 del sitio</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <ResourceRow icon={Building2} label="Render worker" value="render-demo" />
              <ResourceRow icon={Database} label="D1 database" value="cms-demo-db" />
              <ResourceRow icon={HardDrive} label="R2 bucket" value="cms-demo-media" />
              <ResourceRow icon={Globe} label="Custom domain" value="Sin asignar" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle>Logs recientes</CardTitle>
              <CardDescription>Últimos eventos del worker</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Nivel</TableHead>
                    <TableHead>Mensaje</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.logs.map((log, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-muted-foreground">
                        {formatRelative(log.ts)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            log.level === 'error'
                              ? 'destructive'
                              : log.level === 'warn'
                                ? 'warning'
                                : 'secondary'
                          }
                        >
                          {log.level}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {log.message}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="billing">
          <Card>
            <CardHeader>
              <CardTitle>Facturación</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">Plan</p>
                  <p className="text-lg font-semibold">{data?.billing.plan}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Importe</p>
                  <p className="text-lg font-semibold">
                    {data?.billing.amount} €/mes
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Próxima renovación</p>
                  <p className="text-lg font-semibold">
                    {data ? formatRelative(data.billing.renewsAt) : '—'}
                  </p>
                </div>
                <div className="flex items-end justify-end">
                  <Button>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Gestionar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ResourceRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between rounded-md border p-3">
      <div className="flex items-center gap-3">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <span className="font-mono text-xs text-muted-foreground">{value}</span>
    </div>
  );
}

function NewSitePlaceholder() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Crear nuevo sitio</CardTitle>
        <CardDescription>Wizard de onboarding</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Aquí iría el wizard completo de creación de sitios (sprint 7).
        </p>
        <Button asChild className="mt-4">
          <Link to="/sites">Volver al listado</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
